import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FileStack, Pencil, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import FormModalLayout from '../../components/Common/FormModalLayout';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import { useAuth } from '../../contexts/AuthContext';
import { identityService, licenseService, processService } from '../../services/api';
import { userFacingError } from '../../i18n/userFacingError';
import { fetchAllPages } from '../../utils/apiPagination';

function unwrap(payload) {
  return Array.isArray(payload) ? payload : payload?.data ?? payload?.items ?? [];
}

const TABS = [
  { id: 'solicitacoes', label: 'Solicitações' },
  { id: 'eventos', label: 'Eventos' },
  { id: 'certidoes', label: 'Certidões' },
  { id: 'documentos', label: 'Documentos' },
];

const emptyForm = {
  codigo: '',
  nome: '',
  tipo_evento_id: '',
  novo_tipo_evento: '',
  tipo_certidao_id: '',
  novo_tipo_certidao: '',
  tipo_documento_id: '',
  requerente_id: '',
  local_id: '',
  processo_id: '',
  licenca_id: '',
  situacao: '1',
  data_inicio: '',
  data_termino: '',
  data_entrada: '',
  numero_viabilidade: '',
  informacao: '',
  status: '0',
  usuario_id: '',
};

const LicenseSubmodules = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const preLicenca = searchParams.get('licenca') || '';
  const [tab, setTab] = useState(preLicenca ? 'documentos' : 'solicitacoes');
  const [horarioEventoId, setHorarioEventoId] = useState('');
  const [horarioForm, setHorarioForm] = useState({ data: '', horario_inicio: '', horario_termino: '' });
  const [docFile, setDocFile] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm, licenca_id: preLicenca });
  const [docLicencaId, setDocLicencaId] = useState(preLicenca);

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...emptyForm, licenca_id: docLicencaId || preLicenca });
    setDocFile(null);
  };

  const dateVal = (v) => (v ? String(v).slice(0, 10) : '');

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, licenca_id: docLicencaId || preLicenca, processo_id: '' });
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      ...emptyForm,
      codigo: row.codigo || '',
      nome: row.nome || '',
      tipo_evento_id: row.tipo_evento_id || '',
      tipo_certidao_id: row.tipo_certidao_id || '',
      tipo_documento_id: row.tipo_documento_id || '',
      requerente_id: row.requerente_id || '',
      local_id: row.local_id || '',
      processo_id: row.processo_id || '',
      licenca_id: row.licenca_id || docLicencaId || preLicenca,
      situacao: row.situacao != null ? String(row.situacao) : '1',
      data_inicio: dateVal(row.data_inicio),
      data_termino: dateVal(row.data_termino),
      data_entrada: dateVal(row.data_entrada),
      numero_viabilidade: row.numero_viabilidade || '',
      informacao: row.informacao || '',
      status: row.status != null ? String(row.status) : '0',
      usuario_id: row.usuario_id || '',
    });
    setShowForm(true);
  };

  const { data: procRaw } = useQuery(['sub-processos'], () =>
    fetchAllPages((params) => processService.getProcessos(params))
  );
  const { data: clientRaw } = useQuery(['sub-clients'], () =>
    fetchAllPages((params) => identityService.getClients(params))
  );
  const { data: entRaw } = useQuery(['sub-enterprises'], () =>
    fetchAllPages((params) => identityService.getEnterprises(params))
  );
  const { data: licRaw } = useQuery(['sub-licencas'], () =>
    fetchAllPages((params) => licenseService.getLicencas(params))
  );
  const { data: midiaRaw } = useQuery(['sub-midias'], () =>
    fetchAllPages((params) => processService.getTiposMidia(params))
  );
  const { data: tiposEventoRaw } = useQuery(['tipos-evento'], () =>
    licenseService.listTiposEvento().then((r) => r.data)
  );
  const { data: tiposCertidaoRaw } = useQuery(['tipos-certidao'], () =>
    licenseService.listTiposCertidao().then((r) => r.data)
  );

  const processos = unwrap(procRaw);
  const clients = unwrap(clientRaw);
  const enterprises = unwrap(entRaw);
  const licencas = unwrap(licRaw);
  const midias = unwrap(midiaRaw);
  const tiposEvento = unwrap(tiposEventoRaw);
  const tiposCertidao = unwrap(tiposCertidaoRaw);

  const fetchers = useMemo(
    () => ({
      solicitacoes: () => fetchAllPages((params) => licenseService.getSolicitacoes(params)).then((data) => ({ data })),
      eventos: () => fetchAllPages((params) => licenseService.getEventos(params)).then((data) => ({ data })),
      certidoes: () => fetchAllPages((params) => licenseService.getCertidoes(params)).then((data) => ({ data })),
      documentos: () =>
        docLicencaId
          ? licenseService.getDocumentosLicenca(docLicencaId)
          : Promise.resolve({ data: [] }),
    }),
    [docLicencaId]
  );

  const { data, isLoading, error } = useQuery(
    ['license-sub', tab, docLicencaId],
    () => fetchers[tab]().then((r) => r.data)
  );
  const items = unwrap(data);
  const { data: horariosRaw } = useQuery(
    ['horarios-evento', horarioEventoId],
    () => licenseService.listHorariosEvento(horarioEventoId).then((r) => r.data),
    { enabled: tab === 'eventos' && Boolean(horarioEventoId) }
  );
  const horarios = unwrap(horariosRaw);
  const saveHorario = useMutation(
    () =>
      licenseService.createHorarioEvento({
        evento_id: horarioEventoId,
        data: horarioForm.data || null,
        horario_inicio: horarioForm.horario_inicio || null,
        horario_termino: horarioForm.horario_termino || null,
      }),
    {
      onSuccess: () => {
        toast.success('Horário gravado');
        setHorarioForm({ data: '', horario_inicio: '', horario_termino: '' });
        queryClient.invalidateQueries(['horarios-evento', horarioEventoId]);
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  const buildPayload = () => {
    if (tab === 'solicitacoes') {
      const usuarioId = form.usuario_id || user?.id || user?.user_id;
      if (!form.processo_id || !usuarioId) throw new Error('Selecione o processo (usuário da sessão obrigatório).');
      return {
        codigo: form.codigo.trim() || null,
        processo_id: form.processo_id,
        usuario_id: String(usuarioId),
      };
    }
    if (tab === 'eventos') {
      if ((!form.tipo_evento_id && !form.novo_tipo_evento.trim()) || !form.requerente_id || !form.local_id || !form.processo_id) {
        throw new Error('Preencha tipo, requerente, local e processo.');
      }
      return {
        nome: form.nome.trim() || null,
        tipo_evento_id: form.tipo_evento_id.trim(),
        requerente_id: form.requerente_id,
        local_id: form.local_id,
        processo_id: form.processo_id,
        situacao: Number(form.situacao) || 1,
        data_inicio: form.data_inicio || null,
        data_termino: form.data_termino || null,
        numero_viabilidade: form.numero_viabilidade.trim() || null,
      };
    }
    if (tab === 'certidoes') {
      if ((!form.tipo_certidao_id && !form.novo_tipo_certidao.trim()) || !form.requerente_id || !form.processo_id || !form.local_id || !form.data_entrada) {
        throw new Error('Preencha tipo, requerente, processo, local e data de entrada.');
      }
      return {
        tipo_certidao_id: form.tipo_certidao_id.trim(),
        requerente_id: form.requerente_id,
        processo_id: form.processo_id,
        local_id: form.local_id,
        data_entrada: form.data_entrada,
        numero_viabilidade: form.numero_viabilidade.trim() || null,
      };
    }
    if (!form.licenca_id || !form.tipo_documento_id) {
      throw new Error('Selecione a licença e o tipo de documento.');
    }
    return {
      licenca_id: form.licenca_id,
      tipo_documento_id: form.tipo_documento_id,
      status: form.status || '0',
      informacao: form.informacao.trim() || null,
    };
  };

  const save = useMutation(
    async () => {
      const payload = buildPayload();
      if (tab === 'eventos' && !payload.tipo_evento_id && form.novo_tipo_evento.trim()) {
        const created = await licenseService.createTipoEvento({ nome: form.novo_tipo_evento.trim() });
        payload.tipo_evento_id = created.data?.id;
        queryClient.invalidateQueries(['tipos-evento']);
      }
      if (tab === 'certidoes' && !payload.tipo_certidao_id && form.novo_tipo_certidao.trim()) {
        const created = await licenseService.createTipoCertidao({ nome: form.novo_tipo_certidao.trim() });
        payload.tipo_certidao_id = created.data?.id;
        queryClient.invalidateQueries(['tipos-certidao']);
      }
      if (tab === 'solicitacoes') {
        return editingId
          ? licenseService.updateSolicitacao(editingId, payload)
          : licenseService.createSolicitacao(payload);
      }
      if (tab === 'eventos') {
        return editingId ? licenseService.updateEvento(editingId, payload) : licenseService.createEvento(payload);
      }
      if (tab === 'certidoes') {
        return editingId ? licenseService.updateCertidao(editingId, payload) : licenseService.createCertidao(payload);
      }
      if (!editingId) {
        if (!docFile) throw new Error('Anexe o arquivo do documento.');
        return licenseService.uploadDocumentoLicenca(
          form.licenca_id,
          form.tipo_documento_id,
          docFile,
          form.informacao.trim()
        );
      }
      return licenseService.updateDocumentoLicenca(editingId, payload);
    },
    {
      onSuccess: () => {
        toast.success(editingId ? 'Registro atualizado.' : 'Registro criado.');
        closeForm();
        queryClient.invalidateQueries(['license-sub']);
      },
      onError: (e) => toast.error(e?.message || userFacingError(e, 'saveError')),
    }
  );

  const remove = useMutation(
    async (id) => {
      if (tab === 'solicitacoes') return licenseService.deleteSolicitacao(id);
      if (tab === 'eventos') return licenseService.deleteEvento(id);
      if (tab === 'certidoes') return licenseService.deleteCertidao(id);
      return licenseService.deleteDocumentoLicenca(id);
    },
    {
      onSuccess: () => {
        toast.success('Registro removido.');
        queryClient.invalidateQueries(['license-sub']);
      },
      onError: (e) => toast.error(e?.message || userFacingError(e, 'deleteError')),
    }
  );

  const labelFor = (row) =>
    row.nome || row.codigo || row.numero_viabilidade || row.informacao || row.arquivo || row.id;

  const handleDelete = (row) => {
    if (!window.confirm(`Remover “${labelFor(row)}”?`)) return;
    remove.mutate(row.id);
  };

  return (
    <FormPageShell>
      <PageHeader
        title="Eventos, solicitações, certidões e documentos"
        description="Cadastro completo de solicitações, eventos, certidões e documentos vinculados a processos e licenças."
        breadcrumbs={[
          { label: 'Início', to: getAppHomePath() },
          { label: 'Licenças', to: '/licenses' },
          { label: 'Submódulos' },
        ]}
      >
        <Can permission={P.LICENSE_LICENCAS}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={openCreate}
          >
            Novo
          </button>
        </Can>
      </PageHeader>
      <div className="mt-4 flex flex-wrap gap-2">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={tab === item.id ? 'btn btn-primary' : 'btn btn-outline'}
            onClick={() => {
              if (showForm) closeForm();
              setTab(item.id);
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
      {tab === 'documentos' ? (
        <div className="mt-4 max-w-md">
          <label className="form-label">Licença</label>
          <select className="form-select" value={docLicencaId} onChange={(e) => setDocLicencaId(e.target.value)}>
            <option value="">Selecione para listar documentos</option>
            {licencas.map((lic) => (
              <option key={lic.id} value={lic.id}>
                {lic.numero || lic.id}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <div className="data-list-card mt-6 overflow-hidden">
        {isLoading ? (
          <p className="px-6 py-8 text-gray-500">Carregando...</p>
        ) : error ? (
          <p className="px-6 py-8 text-red-600">Não foi possível carregar estes registros.</p>
        ) : items.length === 0 ? (
          <p className="px-6 py-8 text-gray-500">
            <FileStack className="mb-2 inline h-5 w-5" /> Nenhum registro. Use Novo para cadastrar.
          </p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Identificação</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Processo / licença</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Situação</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase text-gray-500">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {items.map((row) => (
                <tr key={row.id}>
                  <td className="px-6 py-4 text-sm text-gray-900">{labelFor(row)}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{row.processo_id || row.licenca_id || '—'}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{row.situacao ?? row.status ?? '—'}</td>
                  <td className="px-6 py-4 text-right text-sm">
                    <Can permission={P.LICENSE_LICENCAS}>
                      {tab === 'eventos' ? (
                        <button
                          type="button"
                          className="mr-3 text-primary-700"
                          onClick={() => setHorarioEventoId(row.id)}
                        >
                          Horários
                        </button>
                      ) : null}
                      {tab === 'documentos' && String(row.arquivo || '').includes('/') ? (
                        <button
                          type="button"
                          className="mr-3 text-primary-700"
                          onClick={async () => {
                            try {
                              const { data: blob } = await licenseService.downloadDocumentoLicenca(row.id);
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = String(row.arquivo).split('/').pop() || 'documento';
                              a.click();
                              URL.revokeObjectURL(url);
                            } catch (e) {
                              toast.error(userFacingError(e, 'loadError'));
                            }
                          }}
                        >
                          Baixar
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="mr-3 text-primary-600 hover:text-primary-800"
                        title="Editar"
                        onClick={() => openEdit(row)}
                      >
                        <Pencil className="inline h-4 w-4" />
                        <span className="sr-only">Editar</span>
                      </button>
                      <button
                        type="button"
                        className="text-red-600 hover:text-red-800"
                        title="Excluir"
                        disabled={remove.isLoading}
                        onClick={() => handleDelete(row)}
                      >
                        <Trash2 className="inline h-4 w-4" />
                        <span className="sr-only">Excluir</span>
                      </button>
                    </Can>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm ? (
        <FormModalLayout
          title={`${editingId ? 'Editar' : 'Novo'} — ${TABS.find((t) => t.id === tab)?.label}`}
          subtitle="Campos exigidos pelo DTO do license-service."
          onClose={closeForm}
          historyEntityType={
            tab === 'solicitacoes'
              ? 'license_requests'
              : tab === 'eventos'
                ? 'license_events'
                : tab === 'certidoes'
                  ? 'viability_certificates'
                  : 'license_documents'
          }
          historyEntityId={editingId}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={closeForm}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" disabled={save.isLoading} onClick={() => save.mutate()}>
                {save.isLoading ? 'Salvando…' : 'Salvar'}
              </button>
            </>
          }
        >
          <div className="form-stack space-y-3">
            {tab === 'solicitacoes' ? (
              <>
                <label className="form-label">Código</label>
                <input className="form-input" value={form.codigo} onChange={(e) => setForm((f) => ({ ...f, codigo: e.target.value }))} />
                <label className="form-label">Processo</label>
                <select className="form-select" value={form.processo_id} onChange={(e) => setForm((f) => ({ ...f, processo_id: e.target.value }))}>
                  <option value="">Selecione</option>
                  {processos.map((p) => (
                    <option key={p.id} value={p.id}>{p.numero || p.id}</option>
                  ))}
                </select>
              </>
            ) : null}
            {tab === 'eventos' ? (
              <>
                <label className="form-label">Nome</label>
                <input className="form-input" value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} />
                <label className="form-label">Tipo de evento</label>
                <select className="form-select" value={form.tipo_evento_id} onChange={(e) => setForm((f) => ({ ...f, tipo_evento_id: e.target.value }))}>
                  <option value="">Selecione ou cadastre abaixo</option>
                  {tiposEvento.map((tipo) => (
                    <option key={tipo.id} value={tipo.id}>{tipo.nome}</option>
                  ))}
                </select>
                <label className="form-label">Novo tipo de evento</label>
                <input className="form-input" value={form.novo_tipo_evento} onChange={(e) => setForm((f) => ({ ...f, novo_tipo_evento: e.target.value }))} placeholder="Nome, se ainda não existir" />
                <label className="form-label">Requerente</label>
                <select className="form-select" value={form.requerente_id} onChange={(e) => setForm((f) => ({ ...f, requerente_id: e.target.value }))}>
                  <option value="">Selecione</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name || c.id}</option>
                  ))}
                </select>
                <label className="form-label">Local (empreendimento)</label>
                <select className="form-select" value={form.local_id} onChange={(e) => setForm((f) => ({ ...f, local_id: e.target.value }))}>
                  <option value="">Selecione</option>
                  {enterprises.map((e) => (
                    <option key={e.id} value={e.id}>{e.name || e.nome || e.id}</option>
                  ))}
                </select>
                <label className="form-label">Processo</label>
                <select className="form-select" value={form.processo_id} onChange={(e) => setForm((f) => ({ ...f, processo_id: e.target.value }))}>
                  <option value="">Selecione</option>
                  {processos.map((p) => (
                    <option key={p.id} value={p.id}>{p.numero || p.id}</option>
                  ))}
                </select>
                <label className="form-label">Início / término</label>
                <div className="grid grid-cols-2 gap-2">
                  <input type="date" className="form-input" value={form.data_inicio} onChange={(e) => setForm((f) => ({ ...f, data_inicio: e.target.value }))} />
                  <input type="date" className="form-input" value={form.data_termino} onChange={(e) => setForm((f) => ({ ...f, data_termino: e.target.value }))} />
                </div>
              </>
            ) : null}
            {tab === 'certidoes' ? (
              <>
                <label className="form-label">Tipo de certidão</label>
                <select className="form-select" value={form.tipo_certidao_id} onChange={(e) => setForm((f) => ({ ...f, tipo_certidao_id: e.target.value }))}>
                  <option value="">Selecione ou cadastre abaixo</option>
                  {tiposCertidao.map((tipo) => (
                    <option key={tipo.id} value={tipo.id}>{tipo.nome}</option>
                  ))}
                </select>
                <label className="form-label">Novo tipo de certidão</label>
                <input className="form-input" value={form.novo_tipo_certidao} onChange={(e) => setForm((f) => ({ ...f, novo_tipo_certidao: e.target.value }))} placeholder="Nome, se ainda não existir" />
                <label className="form-label">Número de viabilidade</label>
                <input className="form-input" value={form.numero_viabilidade} onChange={(e) => setForm((f) => ({ ...f, numero_viabilidade: e.target.value }))} />
                <label className="form-label">Requerente</label>
                <select className="form-select" value={form.requerente_id} onChange={(e) => setForm((f) => ({ ...f, requerente_id: e.target.value }))}>
                  <option value="">Selecione</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name || c.id}</option>
                  ))}
                </select>
                <label className="form-label">Local</label>
                <select className="form-select" value={form.local_id} onChange={(e) => setForm((f) => ({ ...f, local_id: e.target.value }))}>
                  <option value="">Selecione</option>
                  {enterprises.map((e) => (
                    <option key={e.id} value={e.id}>{e.name || e.nome || e.id}</option>
                  ))}
                </select>
                <label className="form-label">Processo</label>
                <select className="form-select" value={form.processo_id} onChange={(e) => setForm((f) => ({ ...f, processo_id: e.target.value }))}>
                  <option value="">Selecione</option>
                  {processos.map((p) => (
                    <option key={p.id} value={p.id}>{p.numero || p.id}</option>
                  ))}
                </select>
                <label className="form-label">Data de entrada</label>
                <input type="date" className="form-input" value={form.data_entrada} onChange={(e) => setForm((f) => ({ ...f, data_entrada: e.target.value }))} />
              </>
            ) : null}
            {tab === 'documentos' ? (
              <>
                <label className="form-label">Licença</label>
                <select className="form-select" value={form.licenca_id} onChange={(e) => setForm((f) => ({ ...f, licenca_id: e.target.value }))}>
                  <option value="">Selecione</option>
                  {licencas.map((lic) => (
                    <option key={lic.id} value={lic.id}>{lic.numero || lic.id}</option>
                  ))}
                </select>
                <label className="form-label">Tipo de documento</label>
                <select className="form-select" value={form.tipo_documento_id} onChange={(e) => setForm((f) => ({ ...f, tipo_documento_id: e.target.value }))}>
                  <option value="">Selecione</option>
                  {midias.map((m) => (
                    <option key={m.id} value={m.id}>{m.nome || m.name || m.id}</option>
                  ))}
                </select>
                <label className="form-label">Informação</label>
                <input className="form-input" value={form.informacao} onChange={(e) => setForm((f) => ({ ...f, informacao: e.target.value }))} />
                {editingId ? null : (
                  <>
                    <label className="form-label">Arquivo</label>
                    <input type="file" className="form-input" onChange={(e) => setDocFile(e.target.files?.[0] || null)} />
                  </>
                )}
              </>
            ) : null}
          </div>
        </FormModalLayout>
      ) : null}

      {tab === 'eventos' && horarioEventoId ? (
        <div className="data-list-card mt-6 p-4">
          <p className="text-sm font-semibold">Horários do evento</p>
          <ul className="mt-2 space-y-1 text-sm text-gray-700">
            {horarios.length === 0 ? <li>Nenhum horário neste evento.</li> : null}
            {horarios.map((row) => (
              <li key={row.id} className="flex items-center justify-between gap-3">
                <span>
                  {row.data || '—'} · {row.horario_inicio || '—'} – {row.horario_termino || '—'}
                </span>
                <button
                  type="button"
                  className="text-red-600"
                  onClick={() =>
                    licenseService.deleteHorarioEvento(row.id).then(() => {
                      queryClient.invalidateQueries(['horarios-evento', horarioEventoId]);
                    })
                  }
                >
                  Remover
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-3 grid gap-2 sm:grid-cols-4">
            <input type="date" className="form-input" value={horarioForm.data} onChange={(e) => setHorarioForm((f) => ({ ...f, data: e.target.value }))} />
            <input type="time" className="form-input" value={horarioForm.horario_inicio} onChange={(e) => setHorarioForm((f) => ({ ...f, horario_inicio: e.target.value }))} />
            <input type="time" className="form-input" value={horarioForm.horario_termino} onChange={(e) => setHorarioForm((f) => ({ ...f, horario_termino: e.target.value }))} />
            <button type="button" className="btn btn-primary" disabled={!horarioForm.data || saveHorario.isLoading} onClick={() => saveHorario.mutate()}>
              Adicionar horário
            </button>
          </div>
        </div>
      ) : null}

      <p className="mt-4 text-xs text-gray-500">
        Tipos de evento e de certidão vêm do catálogo do órgão. Documentos usam tipos de mídia do processo.{' '}
        <Link to="/licenses" className="text-primary-700">Voltar às licenças</Link>
      </p>
    </FormPageShell>
  );
};

export default LicenseSubmodules;
